import { benchmark } from 'shared/containers/App/saga';
import {
  call,
  put,
  select,
  takeLatest,
  CallEffect,
  SelectEffect,
  PutEffect,
  delay,
  takeEvery,
} from 'redux-saga/effects';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import { scheduleInstallationActions, selectPALInstallationDates } from '@buy-viasat/redux/src/schedule-installation';
import {
  selectCoordinates,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import clientLogger from '../../utils/clientLogger';
import getInstallationDatesAsync, {
  GetInstallationDatesPayload,
} from '../../providers/requests/fulfillment/getInstallationDates';
import { parseTimeSlotsByDay } from './utils';
import { GET_WORK_ORDER_RETRY_COUNT } from './types';
import { AvailableInstallationDates, SelectedInstallation } from '@buy-viasat/redux/src/schedule-installation';
import { selectCartId } from '@buy-viasat/redux/src/cart';
import {
  selectFulfillmentAgreementId,
  selectSalesAgreementId,
  selectUrlParams,
  selectWorkOrderId,
  Modals,
  QueryParams,
} from '@buy-viasat/redux/src/app';
import { AddressType } from '@buy-viasat/redux/src/address';
import { sanitizeAddressLines } from '@buy-viasat/redux/src/plan';
import { FormFieldState } from '../Checkout/types';
import {
  selectNoAvailableInstallationDates,
  selectSelectedInstallation,
} from '@buy-viasat/redux/src/schedule-installation';
import { Coordinates, Schedule, ScheduleR0, WorkOrderInformation } from '@buy-viasat/types/build/bv';
import { constructWorkOrderInformation } from 'shared/Navigator/utils';
import editInstallationDatesAsync from 'shared/providers/requests/fulfillment/editInstallationDates';
import { appActions } from '@buy-viasat/redux/src/app';
import { isUnscheduledWorkOrderAsync } from './utils';
import { adobeTargetDataLayer } from 'shared/containers/Analytics';
import { DataLayerEvents } from 'shared/containers/Analytics/types';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import getAppointments, { AvailableAppointments } from 'shared/providers/requests/fulfillment/getAppointments';

export function toIsoDateTime(value?: string): string {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
}

export function addDays(dateTimeIso: string, days: number): string {
  const parsed = new Date(dateTimeIso);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString();
}

export function* getInstallationDatesSaga(
  action: PayloadAction<string>,
): Generator<
  CallEffect | SelectEffect | PutEffect,
  void,
  GetInstallationDatesPayload & AddressType & string & QueryParams & AvailableAppointments & Coordinates & string[]
> {
  const serviceAddress = yield select(selectServiceAddressValues);
  const coordinates = yield select(selectCoordinates);
  const cartId = yield select(selectCartId);
  const { dealerId, partnerId } = yield select(selectUrlParams);
  const salesAgreementId = yield select(selectSalesAgreementId);
  const fulfillmentAgreementId = yield select(selectFulfillmentAgreementId);
  const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  // Dispatch an action to indicate that the installation dates are loading
  yield put(scheduleInstallationActions.setInstallationDatesLoading());

  try {
    let instalationDates;
    const fromDate = toIsoDateTime(action.payload);
    const toDate = addDays(fromDate, 30);

    if (USE_NEW_API) {
      const data: AvailableAppointments = yield call(getAppointments, {
        from: fromDate,
        to: toDate,
        addressLines: serviceAddress.addressLines[0],
        municipality: serviceAddress.municipality,
        region: serviceAddress.region,
        countryCode: serviceAddress.countryCode,
        postalCode: serviceAddress.postalCode,
        latitude: coordinates.latitude + '',
        longitude: coordinates.longitude + '',
        fulfillmentAgreementId: fulfillmentAgreementId,
        shoppingCartId: cartId,
      });
      instalationDates = data;
    } else {
      const { data } = yield call(getInstallationDatesAsync, {
        cartId,
        location: sanitizeAddressLines({ address: serviceAddress, coordinates }, unsanitizedAddressLines),
        from: action.payload,
        dealerId,
        partnerId,
        salesAgreementId,
        fulfillmentAgreementId,
      });
      instalationDates = data?.getInstallationDates || data;
    }

    if (instalationDates?.schedules) {
      const installationDates: AvailableInstallationDates = parseTimeSlotsByDay(
        instalationDates.schedules,
        instalationDates?.timezone ?? '',
      );

      const filteredPalData = (instalationDates.schedules as Array<ScheduleR0 | null | undefined>).filter(
        Boolean,
      ) as ScheduleR0[];

      yield put(scheduleInstallationActions.setPALInstallationDates(filteredPalData)); // Sets PAL data later used to construct work order
      yield put(scheduleInstallationActions.setAvailableInstallationDates(installationDates)); // Sets installation dates displayed in the UI

      const noAvailableInstallationDates = yield select(selectNoAvailableInstallationDates);

      adobeTargetDataLayer.push({
        event: DataLayerEvents.AA_EVENT,
        eventData: {
          event: 'noAvailableInstallationDates',
          scheduleInstallation: {
            noAvailableInstallationDates: noAvailableInstallationDates,
          },
        },
      });
    }
  } catch (e) {
    clientLogger('Error in loadInstallation saga', e);
    yield put(scheduleInstallationActions.getInstallationDatesError());
  } finally {
    // Dispatch an action to indicate that the installation dates loading has completed
    yield put(scheduleInstallationActions.installationDatesLoadingComplete());
  }
}

export function* editInstallationDatesSaga() {
  const workOrderId: string = yield select(selectWorkOrderId);
  const installationDate: FormFieldState<SelectedInstallation> = yield select(selectSelectedInstallation);
  const PALInstallationDates: ScheduleR0[] = yield select(selectPALInstallationDates);
  const workOrderInformation: WorkOrderInformation | null = yield call(
    constructWorkOrderInformation,
    installationDate.value,
    PALInstallationDates,
  );
  const schedule: Schedule = {
    from: workOrderInformation?.appointment?.startTime,
    to: workOrderInformation?.appointment?.endTime,
  };
  try {
    const selectedInstallation: FormFieldState<SelectedInstallation> = yield select(selectSelectedInstallation);
    const {
      value: { month, day, year, arrivalWindow },
    } = selectedInstallation;

    const dateIsSelected = Boolean(month && day && year);
    const timeIsSelected = arrivalWindow !== '';

    const isUnscheduled: boolean = yield call(isUnscheduledWorkOrderAsync, { externalWorkOrderId: workOrderId });
    if (!isUnscheduled) {
      yield put(appActions.setModalVisible(Modals.ERROR));
      yield put(scheduleInstallationActions.installationDatesLoadingComplete());
    } else if (dateIsSelected && timeIsSelected) {
      yield put(appActions.setIsScheduleInstallationComplete(true));
      const { data } = yield call(editInstallationDatesAsync, { externalWorkOrderId: workOrderId, schedule });
      yield put(scheduleInstallationActions.setEditInstallationSuccess(data.editInstallationDate));
    } else {
      yield put(scheduleInstallationActions.setHasSubmittedScheduleInstall(true));
      yield put(scheduleInstallationActions.installationDatesLoadingComplete());
    }
  } catch (err: any) {
    yield put(scheduleInstallationActions.setEditInstallationSuccess(false));
  }
}

export function* editInstallationDatesLoadingSaga() {
  let isSuccessful = false;
  let count = 0;
  while (!isSuccessful && count < GET_WORK_ORDER_RETRY_COUNT) {
    yield delay(5000);
    const workOrderId: string = yield select(selectWorkOrderId);
    isSuccessful = yield call(isUnscheduledWorkOrderAsync, { externalWorkOrderId: workOrderId });
    count++;
  }

  yield put(scheduleInstallationActions.installationDatesLoadingComplete());
}

function handleLowerEnvLogging(action: Action | PayloadAction<string>) {
  if (process.env['NODE_ENV'] !== 'production') {
    console.log(action);
  }
}

export function* scheduleInstallationSaga(): Generator {
  // yield takeEvery('*', handleLowerEnvLogging); // Uncomment to enable logging each action dispatch, for debugging purposes
  yield takeLatest(
    scheduleInstallationActions.editInstallationDatesLoading.type,
    benchmark(editInstallationDatesLoadingSaga),
  );
  yield takeLatest(scheduleInstallationActions.editInstallationDates.type, benchmark(editInstallationDatesSaga));
  yield takeLatest(
    scheduleInstallationActions.getInstallationDates.type,
    benchmark<string, CallEffect | SelectEffect | PutEffect, void, GetInstallationDatesPayload & AddressType & string>(
      getInstallationDatesSaga,
    ),
  );
}
